import { useState } from "react";
import { TopBar } from "../../components/TopBar";
import { Modal } from "../../components/Modal";
import { PageHeader } from "../../components/PageHeader";
import { EmptyState } from "../../components/EmptyState";
import { ChipMulti } from "../../components/Controls";
import { useProjects } from "../../context/ProjectContext";
import { useToast } from "../../context/ToastContext";
import { useAsync } from "../../lib/useAsync";
import { api } from "../../api/client";
import { timeAgo, shortDate } from "../../lib/format";
import type { ApiKeyCreated, KeyScope } from "../../api/types";

export function ApiKeys() {
  const { active } = useProjects();
  const pid = active?.id ?? "";
  const toast = useToast();
  const { data, loading, reload } = useAsync(() => api.listKeys(pid), [pid]);

  const [createOpen, setCreateOpen] = useState(false);
  const [created, setCreated] = useState<ApiKeyCreated | null>(null);
  const [revokeId, setRevokeId] = useState<string | null>(null);

  async function revoke() {
    if (!revokeId) return;
    try {
      await api.revokeKey(pid, revokeId);
      toast("Key revoked — effective immediately");
      setRevokeId(null);
      reload();
    } catch (e) {
      toast((e as Error).message, "err");
    }
  }

  return (
    <>
      <TopBar
        crumbs={<b>API keys</b>}
        actions={<button className="btn btn-primary btn-sm" onClick={() => setCreateOpen(true)}>＋ Create key</button>}
      />
      <div className="content">
        <PageHeader
          title="API keys"
          intent="Server-to-server keys for the REST API. Scoped, named, and revocable. We store only a hash — the secret is shown once."
        />

        <div className="card" style={{ padding: "4px 0" }}>
          {loading ? (
            <div className="loading-rows">{[0, 1].map((i) => <div key={i} className="skel" />)}</div>
          ) : (data?.length ?? 0) === 0 ? (
            <EmptyState
              icon="⚿"
              title="No API keys yet"
              action={<button className="btn btn-primary" onClick={() => setCreateOpen(true)}>Create your first key</button>}
            >
              Create a scoped key to call the leaderboard REST API from your game backend.
            </EmptyState>
          ) : (
            <table className="tbl">
              <thead><tr><th>Name</th><th>Key</th><th>Scopes</th><th>Last used</th><th>Created</th><th></th></tr></thead>
              <tbody>
                {data!.map((k) => (
                  <tr key={k.id} style={{ opacity: k.revoked_at ? 0.5 : 1 }}>
                    <td>{k.name}</td>
                    <td className="mono faint">{k.prefix}…{k.revoked_at && <span className="tag tag-coral" style={{ marginLeft: 8 }}>revoked</span>}</td>
                    <td>
                      <div className="row" style={{ gap: 5 }}>
                        {k.scopes.map((s) => <span key={s} className="tag tag-dim">{s}</span>)}
                      </div>
                    </td>
                    <td className="faint">{timeAgo(k.last_used_at)}</td>
                    <td className="faint">{shortDate(k.created_at)}</td>
                    <td style={{ textAlign: "right" }}>
                      {!k.revoked_at && <button className="btn btn-danger btn-sm" onClick={() => setRevokeId(k.id)}>Revoke</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {createOpen && (
        <CreateKeyModal pid={pid} onClose={() => setCreateOpen(false)} onCreated={(k) => { setCreateOpen(false); setCreated(k); reload(); }} />
      )}

      {created && (
        <Modal title="Key created — copy it now" onClose={() => setCreated(null)}>
          <div className="modal-body">
            <div className="warn-box">⚠ This is the only time the full secret is shown. Store it somewhere safe — you can't retrieve it again.</div>
            <div className="field">
              <label>{created.name}</label>
              <div className="secret-box">{created.secret}</div>
            </div>
            <button className="btn btn-ghost" style={{ justifyContent: "center" }} onClick={() => {
              navigator.clipboard?.writeText(created.secret).then(() => toast("Copied to clipboard"));
            }}>Copy secret</button>
          </div>
          <div className="modal-actions">
            <button className="btn btn-primary" onClick={() => setCreated(null)}>I've saved it</button>
          </div>
        </Modal>
      )}

      {revokeId && (
        <Modal title="Revoke this key?" onClose={() => setRevokeId(null)}>
          <div className="modal-body">
            <div className="warn-box">Any service using this key will start getting <span className="mono">401</span> immediately. This cannot be undone.</div>
          </div>
          <div className="modal-actions">
            <button className="btn btn-ghost" onClick={() => setRevokeId(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={revoke}>Revoke key</button>
          </div>
        </Modal>
      )}
    </>
  );
}

function CreateKeyModal({
  pid, onClose, onCreated,
}: { pid: string; onClose: () => void; onCreated: (k: ApiKeyCreated) => void }) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<KeyScope[]>(["read", "write"]);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!scopes.length) { toast("Pick at least one scope", "err"); return; }
    setBusy(true);
    try {
      onCreated(await api.createKey(pid, name.trim(), scopes));
    } catch (ex) {
      toast((ex as Error).message, "err");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Create API key" onClose={onClose}>
      <form className="modal-body" onSubmit={submit}>
        <div className="field">
          <label htmlFor="kn">Name</label>
          <input id="kn" className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Production server" required />
        </div>
        <div className="field">
          <label>Scopes</label>
          <ChipMulti<KeyScope>
            selected={scopes}
            onChange={setScopes}
            options={[
              { value: "read", label: "read" },
              { value: "write", label: "write" },
              { value: "admin", label: "admin" },
            ]}
          />
          <span className="hint">read = queries; write = submit scores; admin = create/configure boards &amp; keys.</span>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={busy || !name.trim()}>{busy ? <span className="spinner" /> : "Create key"}</button>
        </div>
      </form>
    </Modal>
  );
}
